import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Formik, Field, FieldArray } from 'formik';
import { coreSelectors, coreActions, ResourceType } from '@gymcloud/core';
import { Modal, Button, CheckboxField } from 'web-components';
import { Flex, Text } from 'web-styled';
import * as S from './styled';

const BackIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="none"
    viewBox="0 0 16 16"
  >
    <path
      stroke="#292929"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M10.25 3.5 5.75 8l4.5 4.5"
    ></path>
  </svg>
)

interface Props {
  isOpen: boolean;
  onRequestClose: () => void;
}

const MAX_SELECTED_CLIENTS = 3;

const mapState = (state) => {
  const clients = coreSelectors.getResourceMappedList('clients')(state)
  return {
    clients: clients.filter(c => !c.is_dummy),
  }
}

const ForceArchiveClientsModal = ({ isOpen, onRequestClose }: Props) => {
  const { clients } = useSelector(mapState);
  const dispatch = useDispatch()

  const initialValues = useMemo(() => {
    return {
      ids: clients.reduce<Record<string, boolean>>((acc, client) => {
        acc[client.id] = false;
        return acc;
      }, {}),
    };
  }, [clients]);

  const getSelectedCount = (values: Record<string, boolean>) => {
    return Object.values(values).filter(Boolean).length;
  };

  const handleSubmit = (values: any, callbacks: any) => {
    const selectedIds = Object.entries(values.ids)
      .filter(([_, isSelected]) => !isSelected)
      .map(([id]) => Number(id));

    dispatch(
      coreActions.requestBulkArchiveClients({
        ids: selectedIds,
        callbacks: {
          ...callbacks,
          onSuccess: () => {
            dispatch(
              coreActions.setProfileState({
                show_resubscribe_popup: false
              }),
            )
            onRequestClose();
          },
        },
      })
    )
  }

  return (
    <Modal
      title="Archive Clients"
      contentStyle={{ width: '740px' }}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
    >
      <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
        {(formikProps) => {
          const selectedCount = getSelectedCount(formikProps.values.ids);
          const isMaxSelected = selectedCount >= MAX_SELECTED_CLIENTS;
          const isValidSelection = selectedCount > 0 && selectedCount <= MAX_SELECTED_CLIENTS;

          return (
            <Flex column as="form" width="100%" onSubmit={formikProps.handleSubmit}>
              <Flex column spacing={10} width="100%">
                <Text size="1.72em" weight="700" color="#121827" whiteSpace="pre-line">
                  Select Your Clients
                </Text>
                <Text color="#121827" whiteSpace="pre-line">
                  Choose the {MAX_SELECTED_CLIENTS} clients you wish to remain active. Once you finish, you will be able to access the app normally.
                  Your other clients will be archived automatically, and can be easily restored if you upgrade in the future.
                </Text>
              </Flex>

              <FieldArray name="ids">
                {() => (
                  <S.Clients>
                    {clients.map((client) => {
                      const fieldName = `ids.${client.id}`;
                      const isChecked = formikProps.values.ids[client.id];
                      const isDisabled = !isChecked && isMaxSelected;
                      const fullName = `${client.user_profile.first_name} ${client.user_profile.last_name || ''}`.trim();

                      return (
                        <Field
                          key={client.id}
                          name={fieldName}
                          component={CheckboxField}
                          disabled={isDisabled}
                          label={
                            <Text size="16px" color={isDisabled ? '#999' : '#121827'}>
                              {fullName}
                            </Text>
                          }
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            formikProps.setFieldValue(fieldName, e.target.checked);
                          }}
                        />
                      );
                    })}
                  </S.Clients>
                )}
              </FieldArray>

              <S.Footer>
                <S.BackButton icon={BackIcon} onClick={onRequestClose}>
                  Back
                </S.BackButton>
                <Button
                  type="submit"
                  spacing="large"
                  appearance="success"
                  isDisabled={!isValidSelection}
                  isLoading={formikProps.isSubmitting}
                >
                  Finish
                </Button>
              </S.Footer>
            </Flex>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default ForceArchiveClientsModal;
